import { Briefcase, LayoutGrid, Search, Heart, Star, Building2, Video, Inbox, User, ClipboardList } from 'lucide-react';

export type RecruiterNavKey =
  | 'hiring'
  | 'pipeline'
  | 'candidate-search'
  | 'interested'
  | 'inbox'
  | 'spotlights'
  | 'applications'
  | 'interviews'
  | 'company'
  | 'profile';

/** Sidebar sections, grouped along the recruiting funnel. */
export type RecruiterNavSection = 'sourcing' | 'screening' | 'account';

export interface RecruiterNavItem {
  key: RecruiterNavKey;
  to: string;
  /** i18n key in the `common` namespace (recruiterNav.*). */
  labelKey: string;
  /** Which sidebar section this item belongs to. */
  section: RecruiterNavSection;
  Icon: React.ComponentType<{ className?: string }>;
  /** Legacy pill palette (mobile RecruiterNavPills). */
  activeColor: string;
  hoverColor: string;
}

/** Ordered sidebar sections with their i18n label keys (common namespace). */
export const RECRUITER_NAV_SECTIONS: { id: RecruiterNavSection; labelKey: string }[] = [
  { id: 'sourcing', labelKey: 'recruiterNav.sectionSourcing' },
  { id: 'screening', labelKey: 'recruiterNav.sectionScreening' },
  { id: 'account', labelKey: 'recruiterNav.sectionAccount' },
];

/**
 * Single source of truth for recruiter navigation destinations.
 * Consumed by the desktop RecruiterShell sidebar AND the legacy mobile
 * RecruiterNavPills, so links stay in sync across the B2B surface.
 * Order follows the recruiting funnel: source → hire → manage.
 */
export const RECRUITER_NAV_ITEMS: RecruiterNavItem[] = [
  { key: 'hiring', to: '/?tab=hire', labelKey: 'recruiterNav.hiring', section: 'sourcing', Icon: Briefcase, activeColor: 'text-pink-600 bg-pink-50', hoverColor: 'hover:text-pink-600 hover:bg-pink-50/60' },
  { key: 'candidate-search', to: '/candidate-search', labelKey: 'recruiterNav.candidateSearch', section: 'sourcing', Icon: Search, activeColor: 'text-pink-600 bg-pink-50', hoverColor: 'hover:text-pink-600 hover:bg-pink-50/60' },
  { key: 'spotlights', to: '/spotlights', labelKey: 'recruiterNav.spotlights', section: 'sourcing', Icon: Star, activeColor: 'text-pink-600 bg-pink-50', hoverColor: 'hover:text-pink-600 hover:bg-pink-50/60' },
  { key: 'interested', to: '/interested', labelKey: 'recruiterNav.interested', section: 'sourcing', Icon: Heart, activeColor: 'text-pink-600 bg-pink-50', hoverColor: 'hover:text-pink-600 hover:bg-pink-50/60' },
  { key: 'pipeline', to: '/pipeline', labelKey: 'recruiterNav.pipeline', section: 'sourcing', Icon: LayoutGrid, activeColor: 'text-pink-600 bg-pink-50', hoverColor: 'hover:text-pink-600 hover:bg-pink-50/60' },
  { key: 'inbox', to: '/recruiter-inbox', labelKey: 'recruiterNav.inbox', section: 'sourcing', Icon: Inbox, activeColor: 'text-pink-600 bg-pink-50', hoverColor: 'hover:text-pink-600 hover:bg-pink-50/60' },
  { key: 'applications', to: '/applications', labelKey: 'recruiterNav.applications', section: 'screening', Icon: ClipboardList, activeColor: 'text-pink-600 bg-pink-50', hoverColor: 'hover:text-pink-600 hover:bg-pink-50/60' },
  { key: 'interviews', to: '/interviews', labelKey: 'recruiterNav.interviews', section: 'screening', Icon: Video, activeColor: 'text-pink-600 bg-pink-50', hoverColor: 'hover:text-pink-600 hover:bg-pink-50/60' },
  { key: 'company', to: '/company/manage', labelKey: 'recruiterNav.company', section: 'account', Icon: Building2, activeColor: 'text-pink-600 bg-pink-50', hoverColor: 'hover:text-pink-600 hover:bg-pink-50/60' },
  { key: 'profile', to: '/recruiter-profile', labelKey: 'recruiterNav.profile', section: 'account', Icon: User, activeColor: 'text-pink-600 bg-pink-50', hoverColor: 'hover:text-pink-600 hover:bg-pink-50/60' },
];

/**
 * Resolve the active recruiter nav key from the current location.
 * `/?tab=hire` (query-param home) maps to 'hiring'.
 */
export function resolveRecruiterNavKey(pathname: string, search: string): RecruiterNavKey | null {
  if (pathname === '/' && search.includes('tab=hire')) return 'hiring';
  if (pathname.startsWith('/pipeline')) return 'pipeline';
  if (pathname.startsWith('/candidate-search')) return 'candidate-search';
  if (pathname.startsWith('/interested')) return 'interested';
  if (pathname.startsWith('/recruiter-inbox')) return 'inbox';
  if (pathname.startsWith('/spotlights')) return 'spotlights';
  if (pathname.startsWith('/applications')) return 'applications';
  if (pathname.startsWith('/interviews')) return 'interviews';
  if (pathname.startsWith('/company')) return 'company';
  if (pathname.startsWith('/recruiter-profile')) return 'profile';
  return null;
}
